import React from "react";

/** --- Legend (top only) --- */
function TrayLegend({ drawsMap }) {
  const Swatch = ({ className, label, width = 22, height = 10 }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span
        aria-hidden="true"
        className={className}
        style={{ display: "inline-block", width, height, borderRadius: 4 }}
      />
      <span style={{ fontSize: 12, opacity: 0.9 }}>{label}</span>
    </div>
  );

  return (
    <div
      role="note"
      aria-label="Tray legend"
      className="tray-legend"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 16,
        margin: "8px 0 12px",
        padding: "8px 10px",
        borderRadius: 8,
        background: "rgba(139, 92, 246, 0.06)",
      }}
    >
      <Swatch className="bar bar-vapor" label="Vapor bar (V*)" />
      <Swatch className="bar bar-liquid" label="Liquid bar (1 − V*)" />
      <div style={{ marginLeft: "auto", fontSize: 12, opacity: 0.9 }}>
        φ = vapor fraction
      </div>
    </div>
  );
}

/**
 * ColumnView
 * - Expects:
 *    result.trays: [{ i, T, V, m_vap_up_kgph, m_liq_dn_kgph, ... }]
 *    result.draws: { [1-based tray index]: "Label" }
 *    result.energy.feedRate_kgph: number
 *    drawSpecs: { [0-based tray index]: fraction }
 *    drawTrays:   (optional) array of tray indices you highlight as draws
 */
export default function ColumnView({
  result,
  drawTrays = [],
  drawSpecs = {},
  onLog,
}) {
  const trays = Array.isArray(result?.trays) ? result.trays : [];
  const N = trays.length;
  // Feed tray is stored 1-based (Tray 1 = bottom/reboiler, Tray N = condenser)
  const feedTray1 = Number(result?.feedTray);
  const drawsMap = result?.draws || {};
  const energy = result?.energy || {};
  const feedBasis = Number(energy?.feedRate_kgph) || 0;

  const condenserTypeLabel = String(energy?.condenserType || "total");
  const reboilerTypeLabel = String(energy?.reboilerType || "partial");
  const condenserTitle = `Condenser (Overhead) • ${condenserTypeLabel}`;
  const reboilerTitle = `Reboiler (Bottoms) • ${reboilerTypeLabel}`;

  // ---- spike flags from App (1-based tray indices) ----
  const flags = result?.flags || {};
  const spikeSet = React.useMemo(() => {
    const arr = Array.isArray(flags.spikeTrays) ? flags.spikeTrays : [];
    return new Set(arr);
  }, [flags.spikeTrays]);

  // NOTE: Solver diagnostics are rendered in a dedicated scrollable panel
  // in App.js (above the ColumnView). We intentionally keep ColumnView free
  // of diagnostics UI to avoid duplication.

  // ---- ref to the whole card so we can remove any legacy/bottom legends ----
  const cardRef = React.useRef(null);

  // Remove any duplicate/bottom legends left over from older markup/CSS
  React.useEffect(() => {
    const root = cardRef.current;
    if (!root) return;

    const legends = root.querySelectorAll(".tray-legend");
    if (legends.length > 1) {
      for (let i = 1; i < legends.length; i++) legends[i].remove();
    }
    const oldFooters = root.querySelectorAll(
      ".legend-footer, .legend-bottom, .legend-chips"
    );
    oldFooters.forEach((el) => el.remove());
  }, []);

  // ---------- helpers ----------
  const fmtKgph = (x) => {
    if (!Number.isFinite(x)) return "—";
    const v = Math.abs(x);
    if (v >= 1000000) return `${(x / 1_000_000).toFixed(3)} M kg/h`;
    if (v >= 1000) return `${(x / 1000).toFixed(3)} k kg/h`;
    return `${x.toFixed(0)} kg/h`;
  };
  const fmtPct = (x) => (Number.isFinite(x) ? `${(100 * x).toFixed(1)}%` : "—");

  // Build a robust per-tray draw spec (0-based tray index -> fraction)
  const drawSpec0 = React.useMemo(() => {
    const out = {};
    Object.entries(drawSpecs || {}).forEach(([k, v]) => {
      const i0 = Number.parseInt(k, 10);
      const f = Number(v);
      if (
        Number.isFinite(i0) &&
        i0 >= 0 &&
        i0 < N &&
        Number.isFinite(f) &&
        f > 0
      ) {
        out[i0] = f;
      }
    });
    return out;
  }, [drawSpecs, N]);

  // ---------- Material balance from drawSpec0 ----------
  const mb = React.useMemo(() => {
    if (!feedBasis || !N) return null;

    const items = Object.keys(drawSpec0)
      .map((k) => Number(k))
      .sort((a, b) => b - a)
      .map((i0) => {
        const frac = drawSpec0[i0];
        const kgph = frac * feedBasis;
        const label = drawsMap?.[i0 + 1] || `Draw @ Tray ${i0 + 1}`;
        return { i0, frac, kgph, label };
      });

    const totalKg = items.reduce((s, it) => s + (Number(it.kgph) || 0), 0);
    const totalFrac = items.reduce((s, it) => s + (Number(it.frac) || 0), 0);
    const balanceErrKg = feedBasis - totalKg;

    return { items, totalKg, totalFrac, feedKg: feedBasis, balanceErrKg };
  }, [drawSpec0, drawsMap, feedBasis, N]);

  // ---------- Safe guards for tray list ----------
  const listTopToBottom = React.useMemo(() => trays.slice().reverse(), [trays]);

  // ---------- Bar width helpers ----------
  const widthPct = (x) => `${Math.max(0, Math.min(100, x))}%`;
  const widthFromVapor = (V) => widthPct(100 * (Number(V) || 0)); // 0..100

  // ---------- Render ----------
  return (
    <div className="card column-card" ref={cardRef}>
      <div className="card-header">
        <h3>Column</h3>
      </div>
      <div className="card-body">
        {/* === MATERIAL BALANCE === */}
        {mb && mb.items?.length > 0 && (
          <div className="boundary-card" style={{ marginBottom: 12 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                gap: 12,
              }}
            >
              <div className="boundary-title">
                Material Balance (product basis)
              </div>
              <div className="boundary-duty" style={{ margin: 0 }}>
                <strong>Feed</strong>: {fmtKgph(mb.feedKg)}{" "}
                <span className="muted">({fmtPct(1)})</span>
              </div>
            </div>

            <div className="mb-draws-label">Draws:</div>

            <ul className="kv" style={{ marginTop: 6 }}>
              {mb.items.map((it) => (
                <li key={`mb-${it.i0}`}>
                  <div>
                    <strong>{it.label}</strong>{" "}
                    <span className="muted">[Tray {it.i0 + 1}]</span>
                  </div>
                  <div>
                    {fmtKgph(it.kgph)}{" "}
                    <span className="muted">({fmtPct(it.frac)})</span>
                  </div>
                </li>
              ))}
              <li>
                <div>
                  <strong>Total products</strong>
                </div>
                <div>
                  {fmtKgph(mb.totalKg)}{" "}
                  <span className="muted">({fmtPct(mb.totalFrac)})</span>
                </div>
              </li>
              <li>
                <div>
                  <strong>Balance error</strong>
                </div>
                <div>
                  {fmtKgph(mb.balanceErrKg)}{" "}
                  <span className="muted">
                    {mb.balanceErrKg === 0 ? "(balanced)" : "(feed − products)"}
                  </span>
                </div>
              </li>
            </ul>
          </div>
        )}

        {/* === CONDENSER & REBOILER SUMMARY === */}
        <div className="boundary-grid">
          {/* Condenser */}
          <div className="boundary-card">
            <div className="boundary-header">
              <div className="boundary-title">
                Condenser (Overhead){" "}
                <span className="muted">({condenserTypeLabel})</span>
              </div>
              <div className="boundary-duty">
                <strong>Qc</strong>:{" "}
                {Number.isFinite(energy?.Qc_kW)
                  ? `${energy.Qc_kW.toFixed(1)} kW`
                  : "—"}
              </div>
            </div>
            <div className="boundary-meta small">
              <div>
                <span className="muted">Reflux frac</span>{" "}
                {Number.isFinite(energy?.reflux_fraction)
                  ? `${(100 * energy.reflux_fraction).toFixed(1)}%`
                  : "—"}
              </div>
              <div>
                <span className="muted">T (cold)</span>{" "}
                {Number.isFinite(result?.boundary?.condenser?.T_cold_K)
                  ? `${result.boundary.condenser.T_cold_K.toFixed(1)} K`
                  : "—"}
              </div>
            </div>
          </div>

          {/* Reboiler */}
          <div className="boundary-card">
            <div className="boundary-header">
              <div className="boundary-title">
                Reboiler (Bottoms){" "}
                <span className="muted">({reboilerTypeLabel})</span>
              </div>
              <div className="boundary-duty">
                <strong>Qr</strong>:{" "}
                {Number.isFinite(energy?.Qr_k_W) ||
                Number.isFinite(energy?.Qr_kW)
                  ? `${(energy?.Qr_kW ?? energy?.Qr_k_W).toFixed(1)} kW`
                  : "—"}
              </div>
            </div>
            <div className="boundary-meta small">
              <div>
                <span className="muted">Boil-up frac</span>{" "}
                {Number.isFinite(energy?.boilup_fraction)
                  ? `${(100 * energy.boilup_fraction).toFixed(1)}%`
                  : "—"}
              </div>
              <div>
                <span className="muted">T (hot)</span>{" "}
                {Number.isFinite(result?.boundary?.reboiler?.T_hot_K)
                  ? `${result.boundary.reboiler.T_hot_K.toFixed(1)} K`
                  : "—"}
              </div>
            </div>
          </div>
        </div>

        {/* === LEGEND (top only) === */}
        <TrayLegend drawsMap={drawsMap} />

        {/* Feed tray / flash zone indicator (HYSYS-like hint) */}
        {Number.isFinite(feedTray1) && N > 0 && (
          <div
            className="flashzone-note"
            style={{
              margin: "-4px 0 10px",
              fontSize: 12,
              opacity: 0.9,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          ></div>
        )}

        {/* === OPTIONAL COLUMN-WIDE WARNING === */}
        {spikeSet.size > 0 && (
          <div
            className="quality-banner"
            style={{
              margin: "4px 0 8px",
              padding: "8px 10px",
              borderRadius: 8,
              background: "rgba(255, 193, 7, 0.12)",
              border: "1px solid rgba(255, 193, 7, 0.35)",
              fontSize: 12,
            }}
          >
            ⚠️ Temperature spikes detected on trays:{" "}
            {Array.from(spikeSet).join(", ")}. See Run log for details.
          </div>
        )}

        {/* === TRAYS (top to bottom) === */}
        <ul className="tray-list">
          {listTopToBottom.map((t, idx) => {
            const i0 = N - 1 - idx;
            const isDraw = drawSpec0[i0] > 0;
            const drawPct = drawSpec0[i0] || 0;

            const isReboilerTray = t.i === 1;
            const isCondenserTray = t.i === N;
            const isFlashZone =
              Number.isFinite(feedTray1) &&
              t.i === Math.min(N, Math.max(1, Math.round(feedTray1)));

            const vaporWidth = widthFromVapor(t.V);
            const liquidWidth = widthPct(100 - Math.min(100, 100 * (t.V || 0)));

            const hasReboilerIdentity =
              isReboilerTray &&
              Number.isFinite(t.reboilerFeed_kgph) &&
              Number.isFinite(t.bottomsFromSplit_kgph);

            return (
              <li className="tray-row" key={`tray-${t.i}`}>
                {/* Title */}
                <div className="tray-title">
                  <div>
                    {isReboilerTray
                      ? "Tray 1 (Reboiler)"
                      : isCondenserTray
                      ? `Tray ${N} (Overhead condenser, ${condenserTypeLabel})`
                      : `Tray ${t.i}`}
                    {!isReboilerTray && !isCondenserTray && isFlashZone && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 12,
                          padding: "1px 6px",
                          borderRadius: 999,
                          background: "rgba(56,189,248,0.12)",
                          border: "1px solid rgba(56,189,248,0.30)",
                        }}
                      >
                        Flash zone (feed)
                      </span>
                    )}
                  </div>
                </div>

                {/* Bars */}
                <div className="tray-bars">
                  <div
                    className="bar bar-vapor"
                    style={{ width: vaporWidth }}
                  />
                  <div
                    className="bar bar-liquid"
                    style={{ width: liquidWidth }}
                  />
                </div>

                {/* Meta */}
                <div className="tray-meta mono">
                  <span className="tray-cell">T={(t.T ?? 0).toFixed(2)} K</span>
                  <span className="tray-cell">V*={(t.V ?? 0).toFixed(3)}</span>
                  <span className="tray-cell">
                    V↑={fmtKgph(t.m_vap_up_kgph ?? 0)}
                  </span>
                  <span className="tray-cell">
                    L↓={fmtKgph(t.m_liq_dn_kgph ?? 0)}
                  </span>
                  <span className="tray-cell tray-cell-draw">
                    {isDraw ? `Draw ${fmtPct(drawPct)}` : ""}
                  </span>
                  <span className="tray-cell tray-cell-flag">
                    {spikeSet.has(t.i) ? (
                      <span
                        title="Temperature spike detected: see Run log for details."
                        className="tray-flag"
                      >
                        Spike
                      </span>
                    ) : (
                      ""
                    )}
                  </span>
                </div>

                {/* Extra info line for partial reboiler identity (kept off the main line so bars never collapse) */}
                {hasReboilerIdentity && (
                  <>
                    <div className="tray-title tray-title-sub" />
                    <div className="tray-bars tray-bars-sub" />
                    <div
                      className="tray-meta-sub mono"
                      title="Partial reboiler identity: B = (1−V)·Freb"
                    >
                      B=(1−V)·Freb = {fmtKgph(t.bottomsFromSplit_kgph)} (Freb{" "}
                      {fmtKgph(t.reboilerFeed_kgph)})
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
